import { c as _c } from "react/compiler-runtime";
import Translate from "@mui/icons-material/Translate";
import { alpha, Box, Divider, FormControl, InputLabel, List, ListItem, ListItemButton, ListItemIcon, ListItemText, ListSubheader, MenuItem, Select, SwipeableDrawer, Typography, useTheme } from "@mui/material";
import { signOut, useSession } from "next-auth/react";
import Link from "next/link";
import { useRouter as useAppRouter } from "next/navigation";
import React, { useEffect, useState } from "react";
import { useOptionalCookieConsentContext } from "#/features/cookieConsent/context/CookieConsentContext";
import type { Locales } from "#/i18n/i18n-types";
import { loadLocaleAsync } from "#/i18n/i18n-util.async";
import { localeLabels, localesForLanguagePicker } from "#/i18n/labels";
import { localeBareHomePath } from "#/i18n/localeBareHomePath";
import { parseSelectLocaleValue } from "#/i18n/parseSelectLocaleValue";
import { setStoredLocale } from "#/shared/browser-storage/localeStorage";
import { useI18nContext, usePagesRouterCompat } from "#/shared/hooks";
import { GITHUB_URL } from "#/constants";

/** App Router only — mounted when Pages compat router is absent. */
const AppLocaleHomeRedirect: React.FC<{
  locale: Locales;
}> = t0 => {
  const $ = _c(4);
  const {
    locale
  } = t0;
  const appRouter = useAppRouter();
  let t1;
  let t2;
  if ($[0] !== appRouter || $[1] !== locale) {
    t1 = () => {
      appRouter.push(localeBareHomePath(locale));
    };
    t2 = [appRouter, locale];
    $[0] = appRouter;
    $[1] = locale;
    $[2] = t1;
    $[3] = t2;
  } else {
    t1 = $[2];
    t2 = $[3];
  }
  useEffect(t1, t2);
  return null;
};
type NavItemProps = {
  title: string;
  onClick?: () => void;
  href?: string;
};
const NavItem: React.FC<NavItemProps> = t0 => {
  const $ = _c(7);
  const {
    title,
    onClick,
    href
  } = t0;
  const component = href ? "a" : "button";
  let t1;
  if ($[0] !== title) {
    t1 = <ListItemText primary={title} />;
    $[0] = title;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== component || $[3] !== href || $[4] !== onClick || $[5] !== t1) {
    t2 = <ListItem disablePadding={true}><ListItemButton component={component} href={href} onClick={onClick}>{t1}</ListItemButton></ListItem>;
    $[2] = component;
    $[3] = href;
    $[4] = onClick;
    $[5] = t1;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  return t2;
};
type SidePanelProps = {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
};
export const SidePanel: React.FC<SidePanelProps> = t0 => {
  const $ = _c(92);
  const {
    isOpen,
    setIsOpen
  } = t0;
  const pagesRouter = usePagesRouterCompat();
  const [appLocaleRedirect, setAppLocaleRedirect] = useState(null);
  const {
    LL,
    locale: activeLocale
  } = useI18nContext();
  const theme = useTheme();
  const session = useSession();
  const cookieConsent = useOptionalCookieConsentContext();
  let t1;
  if ($[0] !== cookieConsent || $[1] !== setIsOpen) {
    t1 = () => {
      cookieConsent?.openCookieSettings();
      setIsOpen(false);
    };
    $[0] = cookieConsent;
    $[1] = setIsOpen;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const handleOpenCookieSettings = t1;
  let t2;
  if ($[3] !== activeLocale || $[4] !== pagesRouter) {
    t2 = async newLocale => {
      const currentLocale = pagesRouter?.locale ?? activeLocale;
      if (newLocale === currentLocale) {
        return;
      }
      setStoredLocale(newLocale);
      await loadLocaleAsync(newLocale);
      if (pagesRouter) {
        pagesRouter.push(pagesRouter.asPath, undefined, {
          locale: newLocale
        });
        return;
      }
      setAppLocaleRedirect(newLocale);
    };
    $[3] = activeLocale;
    $[4] = pagesRouter;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const handleChangeLocale = t2;
  let t3;
  if ($[6] !== appLocaleRedirect || $[7] !== pagesRouter) {
    t3 = !pagesRouter && appLocaleRedirect ? <AppLocaleHomeRedirect locale={appLocaleRedirect} /> : null;
    $[6] = appLocaleRedirect;
    $[7] = pagesRouter;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  let t5;
  if ($[9] !== setIsOpen) {
    t4 = () => setIsOpen(true);
    t5 = () => setIsOpen(false);
    $[9] = setIsOpen;
    $[10] = t4;
    $[11] = t5;
  } else {
    t4 = $[10];
    t5 = $[11];
  }
  let t6;
  if ($[12] !== theme.appDesign.surfaceHigh) {
    t6 = alpha(theme.appDesign.surfaceHigh, 0.88);
    $[12] = theme.appDesign.surfaceHigh;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== theme.appDesign.outline) {
    t7 = alpha(theme.appDesign.outline, 0.16);
    $[14] = theme.appDesign.outline;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  const t8 = `1px solid ${t7}`;
  let t9;
  if ($[16] !== t6 || $[17] !== t8) {
    t9 = {
      paper: {
        sx: {
          background: t6,
          backdropFilter: "blur(20px)",
          borderLeft: t8
        }
      }
    };
    $[16] = t6;
    $[17] = t8;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  let t10;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = {
      minWidth: "22vw"
    };
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  let t11;
  if ($[20] !== LL) {
    t11 = LL.MAIN_MENU();
    $[20] = LL;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  let t12;
  if ($[22] !== t11) {
    t12 = <ListSubheader disableSticky={true}>{t11}</ListSubheader>;
    $[22] = t11;
    $[23] = t12;
  } else {
    t12 = $[23];
  }
  const t13 = `/profile/${session.data?.user.id ?? ""}`;
  let t14;
  if ($[24] !== LL) {
    t14 = LL.PROFILE();
    $[24] = LL;
    $[25] = t14;
  } else {
    t14 = $[25];
  }
  let t15;
  if ($[26] !== t14) {
    t15 = <NavItem title={t14} />;
    $[26] = t14;
    $[27] = t15;
  } else {
    t15 = $[27];
  }
  let t16;
  if ($[28] !== t13 || $[29] !== t15) {
    t16 = <Link href={t13}>{t15}</Link>;
    $[28] = t13;
    $[29] = t15;
    $[30] = t16;
  } else {
    t16 = $[30];
  }
  let t17;
  if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <NavItem title="GitHub" href={GITHUB_URL} />;
    $[31] = t17;
  } else {
    t17 = $[31];
  }
  let t18;
  if ($[32] !== LL) {
    t18 = LL.FEEDBACK();
    $[32] = LL;
    $[33] = t18;
  } else {
    t18 = $[33];
  }
  let t19;
  if ($[34] !== t18) {
    t19 = <NavItem title={t18} href={`${GITHUB_URL}/issues`} />;
    $[34] = t18;
    $[35] = t19;
  } else {
    t19 = $[35];
  }
  let t20;
  if ($[36] !== LL) {
    t20 = LL.LOGOUT();
    $[36] = LL;
    $[37] = t20;
  } else {
    t20 = $[37];
  }
  let t21;
  if ($[38] !== t20) {
    t21 = <NavItem title={t20} onClick={_temp} />;
    $[38] = t20;
    $[39] = t21;
  } else {
    t21 = $[39];
  }
  let t22;
  if ($[40] !== t12 || $[41] !== t16 || $[42] !== t19 || $[43] !== t21) {
    t22 = <List subheader={t12}>{t16}{t17}{t19}{t21}</List>;
    $[40] = t12;
    $[41] = t16;
    $[42] = t19;
    $[43] = t21;
    $[44] = t22;
  } else {
    t22 = $[44];
  }
  let t23;
  if ($[45] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = <Divider />;
    $[45] = t23;
  } else {
    t23 = $[45];
  }
  let t24;
  if ($[46] !== LL) {
    t24 = LL.SETTINGS();
    $[46] = LL;
    $[47] = t24;
  } else {
    t24 = $[47];
  }
  let t25;
  if ($[48] !== t24) {
    t25 = <ListSubheader disableSticky={true}>{t24}</ListSubheader>;
    $[48] = t24;
    $[49] = t25;
  } else {
    t25 = $[49];
  }
  let t26;
  if ($[50] === Symbol.for("react.memo_cache_sentinel")) {
    t26 = <ListItemIcon><Translate /></ListItemIcon>;
    $[50] = t26;
  } else {
    t26 = $[50];
  }
  let t27;
  if ($[51] !== LL) {
    t27 = LL.LANGUAGE();
    $[51] = LL;
    $[52] = t27;
  } else {
    t27 = $[52];
  }
  let t28;
  if ($[53] !== t27) {
    t28 = <InputLabel id="side-panel-language-label">{t27}</InputLabel>;
    $[53] = t27;
    $[54] = t28;
  } else {
    t28 = $[54];
  }
  let t29;
  if ($[55] !== LL) {
    t29 = LL.LANGUAGE();
    $[55] = LL;
    $[56] = t29;
  } else {
    t29 = $[56];
  }
  const t30 = pagesRouter?.locale as Locales ?? activeLocale ?? "en";
  let t31;
  if ($[57] !== handleChangeLocale) {
    t31 = event => {
      const newLocale_0 = parseSelectLocaleValue(String(event.target.value));
      if (newLocale_0) {
        handleChangeLocale(newLocale_0);
      }
    };
    $[57] = handleChangeLocale;
    $[58] = t31;
  } else {
    t31 = $[58];
  }
  let t32;
  if ($[59] === Symbol.for("react.memo_cache_sentinel")) {
    t32 = localesForLanguagePicker.map(_temp2);
    $[59] = t32;
  } else {
    t32 = $[59];
  }
  let t33;
  if ($[60] !== t29 || $[61] !== t30 || $[62] !== t31) {
    t33 = <Select labelId="side-panel-language-label" label={t29} value={t30} onChange={t31}>{t32}</Select>;
    $[60] = t29;
    $[61] = t30;
    $[62] = t31;
    $[63] = t33;
  } else {
    t33 = $[63];
  }
  let t34;
  if ($[64] !== t28 || $[65] !== t33) {
    t34 = <ListItem>{t26}<FormControl fullWidth={true}>{t28}{t33}</FormControl></ListItem>;
    $[64] = t28;
    $[65] = t33;
    $[66] = t34;
  } else {
    t34 = $[66];
  }
  let t35;
  if ($[67] !== LL) {
    t35 = LL.PRIVACY_POLICY();
    $[67] = LL;
    $[68] = t35;
  } else {
    t35 = $[68];
  }
  let t36;
  if ($[69] !== t35) {
    t36 = <NavItem title={t35} href="/privacy" />;
    $[69] = t35;
    $[70] = t36;
  } else {
    t36 = $[70];
  }
  let t37;
  if ($[71] !== LL || $[72] !== cookieConsent || $[73] !== handleOpenCookieSettings) {
    t37 = cookieConsent ? <NavItem title={LL.COOKIE_SETTINGS_LINK()} onClick={handleOpenCookieSettings} /> : null;
    $[71] = LL;
    $[72] = cookieConsent;
    $[73] = handleOpenCookieSettings;
    $[74] = t37;
  } else {
    t37 = $[74];
  }
  let t38;
  if ($[75] !== t25 || $[76] !== t34 || $[77] !== t36 || $[78] !== t37) {
    t38 = <List subheader={t25}>{t34}{t36}{t37}</List>;
    $[75] = t25;
    $[76] = t34;
    $[77] = t36;
    $[78] = t37;
    $[79] = t38;
  } else {
    t38 = $[79];
  }
  let t39;
  if ($[80] !== t22 || $[81] !== t38) {
    t39 = <Box role="presentation" sx={t10}>{t22}{t23}{t38}</Box>;
    $[80] = t22;
    $[81] = t38;
    $[82] = t39;
  } else {
    t39 = $[82];
  }
  let t40;
  if ($[83] !== isOpen || $[84] !== t39 || $[85] !== t4 || $[86] !== t5 || $[87] !== t9) {
    t40 = <SwipeableDrawer anchor="right" open={isOpen} onOpen={t4} onClose={t5} slotProps={t9}>{t39}</SwipeableDrawer>;
    $[83] = isOpen;
    $[84] = t39;
    $[85] = t4;
    $[86] = t5;
    $[87] = t9;
    $[88] = t40;
  } else {
    t40 = $[88];
  }
  let t41;
  if ($[89] !== t3 || $[90] !== t40) {
    t41 = <>{t3}{t40}</>;
    $[89] = t3;
    $[90] = t40;
    $[91] = t41;
  } else {
    t41 = $[91];
  }
  return t41;
};
function _temp() {
  return signOut();
}
function _temp2(localeOption) {
  return <MenuItem key={localeOption} value={localeOption}><Typography>{localeLabels[localeOption]}</Typography></MenuItem>;
}